import React from "react";
import { FormikValues } from "formik";

import { URSSAF_FORM, URSSAF_FORM_ERRORS } from "@config/testIds/urssaf";

import { OptionPanel } from "@ui/Atoms";
import { Row, Column } from "@ui/Atoms/Grid";
import { FormikInputWithError } from "@ui/Molecules/FormikInputWithError";
import { FormikSelectExtended } from "@ui/Molecules";

import { useTranslate } from "@hooks/useTranslate";

import {
  StyledColumn,
  Heading,
  FIELDS,
} from "../../UrssafRegistrationForm.helpers";

import { getTitleOptions } from "./BankAccount.helpers";

interface Props {
  values: FormikValues;
  handleChange: (e: React.ChangeEvent<HTMLElement>) => void;
  setFieldValue: (field: FIELDS, value: string | Date | null) => void;
}

const BankAccount = (props: Props): React.ReactElement => {
  const translate = useTranslate("batmaid_pwa_urssaf");
  const translateGeneric = useTranslate("batmaid_pwa_generic");

  const handleSelectChange = ({
    name,
    value,
  }: {
    name: string;
    value: string;
  }) => {
    props.setFieldValue(name as FIELDS, value);
  };

  return (
    <OptionPanel>
      <Row>
        <Column lg={10} centered>
          <Heading>{translate("subsection_account")}</Heading>
          <Row>
            <StyledColumn md={4} lg={2}>
              <FormikSelectExtended
                isSearchable={false}
                name={FIELDS.ACCOUNT_HOLDER_TITLE}
                placeholder={translate("form_account_holder_title")}
                options={getTitleOptions(translateGeneric)}
                value={props.values[FIELDS.ACCOUNT_HOLDER_TITLE]}
                onChange={handleSelectChange}
                testId={URSSAF_FORM.TITLE}
                errorTestId={URSSAF_FORM_ERRORS.TITLE}
              />
            </StyledColumn>
            <StyledColumn md={4} lg={5}>
              <FormikInputWithError
                name={FIELDS.ACCOUNT_HOLDER_FIRSTNAME}
                label={translate("form_account_holder_firstname")}
                value={props.values[FIELDS.ACCOUNT_HOLDER_FIRSTNAME]}
                handleChange={props.handleChange}
                testId={URSSAF_FORM.ACCOUNT_HOLDER_NAME}
                errorTestId={URSSAF_FORM_ERRORS.ACCOUNT_HOLDER_NAME}
              />
            </StyledColumn>
            <StyledColumn md={4} lg={5}>
              <FormikInputWithError
                name={FIELDS.ACCOUNT_HOLDER_LASTNAME}
                label={translate("form_account_holder_lastname")}
                value={props.values[FIELDS.ACCOUNT_HOLDER_LASTNAME]}
                handleChange={props.handleChange}
                testId={URSSAF_FORM.ACCOUNT_HOLDER_SURNAME}
                errorTestId={URSSAF_FORM_ERRORS.ACCOUNT_HOLDER_SURNAME}
              />
            </StyledColumn>
          </Row>

          <Row>
            <StyledColumn md={6}>
              <FormikInputWithError
                name={FIELDS.BIC}
                label={translate("form_bic")}
                value={props.values[FIELDS.BIC]}
                handleChange={props.handleChange}
                testId={URSSAF_FORM.BIC}
                errorTestId={URSSAF_FORM_ERRORS.BIC}
              />
            </StyledColumn>
            <StyledColumn md={6}>
              <FormikInputWithError
                name={FIELDS.IBAN}
                label={translate("form_bank_account_number")}
                value={props.values[FIELDS.IBAN]}
                handleChange={props.handleChange}
                testId={URSSAF_FORM.ACCOUNT_NUMBER}
                errorTestId={URSSAF_FORM_ERRORS.ACCOUNT_NUMBER}
              />
            </StyledColumn>
          </Row>
        </Column>
      </Row>
    </OptionPanel>
  );
};

export { BankAccount };
